import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DBFieldBlock from "../../../component/global/DBFieldBlock";
import DBSelect from "../../../component/global/DBSelect";
import { groupSaleCartsByItemType, normalizeSaleCarts } from "./helpers";

export default function TargetPicker({ carts, kind, selectedId, onChange }) {
    const { t } = useTranslation();
    const normalized = useMemo(() => normalizeSaleCarts(carts), [carts]);
    const groups = useMemo(
        () =>
            groupSaleCartsByItemType(normalized).filter(
                (group) => group.id != null
            ),
        [normalized]
    );

    const selectedCart =
        kind === "item"
            ? normalized.find(
                  (cart) =>
                      String(cart.id) === String(selectedId) ||
                      String(cart.itemId) === String(selectedId)
              )
            : null;

    const itemTypeId =
        kind === "itemType" ? selectedId : selectedCart?.itemTypeId ?? null;

    const typeOptions = groups.map((group) => ({
        value: group.id,
        label: group.name || t("offers.target.unassigned"),
    }));

    const itemOptions = useMemo(() => {
        const group = groups.find(
            (item) => String(item.id) === String(itemTypeId)
        );
        return (group?.carts || []).map((cart) => ({
            value: cart.itemId ?? cart.id,
            label: cart.item,
        }));
    }, [groups, itemTypeId]);

    const itemValue =
        kind === "item"
            ? selectedCart?.itemId ?? selectedCart?.id ?? undefined
            : undefined;

    const handleTypeChange = (value) => {
        onChange({ kind: "itemType", selectedId: value ?? null });
    };

    const handleItemChange = (value) => {
        if (value == null) {
            onChange({ kind: "itemType", selectedId: itemTypeId });
            return;
        }
        onChange({ kind: "item", selectedId: value });
    };

    if (normalized.length === 0) {
        return (
            <div className="offer-target">
                <p className="offer-empty-inline" role="status">
                    {t("offers.editor.noBasket")}
                </p>
            </div>
        );
    }

    return (
        <div className="offer-target">
            <DBFieldBlock
                title={t("offers.editor.itemType")}
                required
                hint={t("offers.editor.targetHint")}
            >
                <DBSelect
                    size="large"
                    showSearch
                    optionFilterProp="label"
                    placeholder={t("form.select")}
                    value={itemTypeId ?? undefined}
                    onChange={handleTypeChange}
                    options={typeOptions}
                    aria-label={t("offers.editor.itemType")}
                />
            </DBFieldBlock>
            <DBFieldBlock title={t("offers.editor.item")}>
                <DBSelect
                    size="large"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder={t("form.select")}
                    value={itemValue}
                    onChange={handleItemChange}
                    options={itemOptions}
                    disabled={!itemTypeId}
                    aria-label={t("offers.editor.item")}
                />
            </DBFieldBlock>
        </div>
    );
}
